import { Wallet, HDNodeWallet, formatEther, parseEther } from 'ethers';
import { createPublicClient, http, formatEther as viemFormatEther } from 'viem';
import { sepolia, mainnet } from 'viem/chains';
import { KeyManager } from '../security';
import type { SecureStorageAdapter } from '../storage/SecureStorage';
import { createSecureStorage, WebEncryptedStorageAdapter } from '../storage/SecureStorage';
import { NETWORKS, DERIVATION_PATHS, getNetworkConfig, type NetworkConfig } from '../config/networks';
import type { NetworkType } from '../types';
import { getWdkApiClient } from '../services/WdkApiClient';
import { WdkService, type MultiChainAddresses as WdkMultiChainAddresses } from '../services/WdkService';

/**
 * Note on Tether WDK Integration:
 *
 * Tether WDK (@tetherto/wdk, @tetherto/wdk-wallet-btc, @tetherto/wdk-wallet-evm)
 * is designed for Node.js and React Native environments. The Bitcoin wallet module
 * uses Electrum client which requires Node.js 'net' module.
 *
 * For browser environments (Next.js, React web), we use ethers.js for address derivation
 * following proper BIP-44/BIP-84 paths. This provides deterministic addresses that are
 * compatible with the same seed used in WDK-based apps.
 *
 * For React Native or Node.js apps, you can integrate WDK directly:
 * @see https://docs.wallet.tether.io/
 */

/**
 * Storage keys for wallet data
 */
const STORAGE_KEYS = {
  ENCRYPTED_SEED: 'encrypted_seed',
  ACTIVE_WALLET: 'active_wallet',
} as const;

/**
 * Supported chains for Zubari WDK Wallet
 */
export const SUPPORTED_CHAINS: NetworkType[] = ['ethereum', 'bitcoin', 'ton', 'tron', 'solana', 'spark'];

/**
 * Chain balance information
 */
export interface ChainBalance {
  chain: NetworkType;
  symbol: string;
  balance: string;
  balanceUsd: number;
  address: string;
  decimals: number;
  icon?: string;
}

/**
 * Multi-chain addresses
 */
export interface MultiChainAddresses {
  ethereum: string;
  bitcoin: string;
  ton: string;
  tron: string;
  solana: string;
  spark: string;
}

/**
 * Wallet state interface
 */
export interface WalletState {
  isInitialized: boolean;
  isLocked: boolean;
  address: string | null;
  balance: string | null;
  // Multi-chain support
  addresses?: Partial<MultiChainAddresses>;
  balances?: ChainBalance[];
  selectedChain?: NetworkType;
}

/**
 * WalletManager Configuration
 */
export interface WalletManagerConfig {
  network?: 'mainnet' | 'testnet';
  rpcUrl?: string;
  storage?: SecureStorageAdapter;
  enabledChains?: NetworkType[];
  /** API URL for WDK backend (for multi-chain address derivation) */
  apiUrl?: string;
}

/**
 * WalletManager - Handles BIP-39 seed generation, address derivation, and secure storage
 *
 * This class provides:
 * - BIP-39 mnemonic generation (12 words)
 * - BIP-44 address derivation for multiple chains (Ethereum, Bitcoin, TON, TRON, Solana, Spark)
 * - Encrypted seed storage using AES-256-GCM
 * - Multi-chain balance fetching
 */
export class WalletManager {
  private readonly config: Required<Omit<WalletManagerConfig, 'enabledChains'>> & { enabledChains: NetworkType[] };
  private storage: SecureStorageAdapter;
  private currentSeed: string | null = null;
  private derivedAddress: string | null = null;
  private derivedAddresses: Partial<MultiChainAddresses> = {};
  private selectedChain: NetworkType = 'ethereum';
  private wdkService: WdkService;

  constructor(config: WalletManagerConfig = {}) {
    // Get the appropriate RPC URL from network config
    const isTestnet = config.network !== 'mainnet';
    const ethereumConfig = getNetworkConfig('ethereum', isTestnet);

    this.config = {
      network: config.network || 'testnet',
      rpcUrl: config.rpcUrl || ethereumConfig.rpcUrl,
      storage: config.storage || createSecureStorage(),
      enabledChains: config.enabledChains || SUPPORTED_CHAINS,
      apiUrl: config.apiUrl || process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001',
    };
    this.storage = this.config.storage;

    // Initialize native WDK service
    this.wdkService = new WdkService({
      network: this.config.network,
    });
  }

  /**
   * Generate a new BIP-39 seed phrase (12 words) using ethers.js
   * For native WDK generation, use generateSeedWithWdk() instead
   */
  static generateSeed(): string {
    const wallet = Wallet.createRandom();
    const mnemonic = wallet.mnemonic;
    if (!mnemonic) {
      throw new Error('Failed to generate mnemonic');
    }
    return mnemonic.phrase;
  }

  /**
   * Generate a new BIP-39 seed phrase using native Tether WDK
   * This is the recommended method for generating seed phrases
   */
  async generateSeedWithWdk(): Promise<string> {
    return await this.wdkService.generateSeedPhrase();
  }

  /**
   * Validate seed phrase using native WDK (async, more accurate)
   */
  async validateSeedWithWdk(seed: string): Promise<boolean> {
    return await this.wdkService.isValidSeed(seed);
  }

  /**
   * Validate a BIP-39 seed phrase
   */
  static validateSeed(seed: string): boolean {
    return KeyManager.validateSeedPhrase(seed);
  }

  /**
   * Derive Ethereum address from seed phrase using BIP-44 path
   * Path: m/44'/60'/0'/0/0
   */
  static deriveAddress(seed: string): string {
    const hdNode = HDNodeWallet.fromPhrase(seed, undefined, "m/44'/60'/0'/0/0");
    return hdNode.address;
  }

  /**
   * Get the HDNodeWallet for signing transactions
   */
  static getWallet(seed: string): HDNodeWallet {
    return HDNodeWallet.fromPhrase(seed, undefined, "m/44'/60'/0'/0/0");
  }

  /**
   * Initialize storage with password (required for web platform)
   */
  async initializeStorage(password: string): Promise<void> {
    if (this.storage instanceof WebEncryptedStorageAdapter) {
      await this.storage.initialize(password);
    }
  }

  /**
   * Create a new wallet with generated seed
   */
  async createWallet(password: string): Promise<{ seed: string; address: string }> {
    const seed = WalletManager.generateSeed();
    const address = WalletManager.deriveAddress(seed);

    // Encrypt and store seed
    const encrypted = await KeyManager.encryptSeed(seed, password);
    await this.storage.setItem(STORAGE_KEYS.ENCRYPTED_SEED, encrypted);

    this.currentSeed = seed;
    this.derivedAddress = address;

    return { seed, address };
  }

  /**
   * Import an existing wallet from seed phrase
   */
  async importWallet(seed: string, password: string): Promise<{ address: string }> {
    if (!WalletManager.validateSeed(seed)) {
      throw new Error('Invalid seed phrase');
    }

    const address = WalletManager.deriveAddress(seed);

    // Encrypt and store seed
    const encrypted = await KeyManager.encryptSeed(seed, password);
    await this.storage.setItem(STORAGE_KEYS.ENCRYPTED_SEED, encrypted);

    this.currentSeed = seed;
    this.derivedAddress = address;

    return { address };
  }

  /**
   * Unlock wallet with password
   */
  async unlock(password: string): Promise<{ address: string }> {
    const encrypted = await this.storage.getItem(STORAGE_KEYS.ENCRYPTED_SEED);
    if (!encrypted) {
      throw new Error('No wallet found');
    }

    try {
      const seed = await KeyManager.decryptSeed(encrypted, password);
      const address = WalletManager.deriveAddress(seed);

      this.currentSeed = seed;
      this.derivedAddress = address;

      return { address };
    } catch {
      throw new Error('Invalid password');
    }
  }

  /**
   * Lock wallet (clear seed from memory)
   */
  lock(): void {
    this.currentSeed = null;
    this.wdkService.dispose();
    // Keep derived address for display
  }

  /**
   * Check if wallet exists in storage
   */
  async hasWallet(): Promise<boolean> {
    return this.storage.hasItem(STORAGE_KEYS.ENCRYPTED_SEED);
  }

  /**
   * Delete wallet from storage
   */
  async deleteWallet(): Promise<void> {
    await this.storage.removeItem(STORAGE_KEYS.ENCRYPTED_SEED);
    this.currentSeed = null;
    this.derivedAddress = null;
    this.derivedAddresses = {};
    this.wdkService.dispose();
  }

  /**
   * Get current wallet state
   */
  getState(): WalletState {
    return {
      isInitialized: this.derivedAddress !== null,
      isLocked: this.currentSeed === null && this.derivedAddress !== null,
      address: this.derivedAddress,
      balance: null, // Use fetchBalance for current balance
    };
  }

  /**
   * Get current address (if unlocked)
   */
  getAddress(): string | null {
    return this.derivedAddress;
  }

  /**
   * Check if wallet is unlocked
   */
  isUnlocked(): boolean {
    return this.currentSeed !== null;
  }

  /**
   * Get the seed phrase (only if unlocked)
   */
  getSeed(): string | null {
    return this.currentSeed;
  }

  /**
   * Fetch balance for current address with timeout handling
   */
  async fetchBalance(): Promise<string> {
    if (!this.derivedAddress) {
      throw new Error('Wallet not initialized');
    }

    const chain = this.config.network === 'mainnet' ? mainnet : sepolia;
    const client = createPublicClient({
      chain,
      transport: http(this.config.rpcUrl, {
        timeout: 15000, // 15 second timeout
        retryCount: 2,
        retryDelay: 1000,
      }),
    });

    try {
      const balance = await client.getBalance({
        address: this.derivedAddress as `0x${string}`,
      });

      return viemFormatEther(balance);
    } catch (error) {
      console.warn('Failed to fetch balance:', error);
      // Return '0' instead of throwing to not block wallet operations
      return '0';
    }
  }

  /**
   * Create viem public client for the current network
   */
  getPublicClient() {
    const chain = this.config.network === 'mainnet' ? mainnet : sepolia;
    return createPublicClient({
      chain,
      transport: http(this.config.rpcUrl, {
        timeout: 15000, // 15 second timeout
        retryCount: 2,
        retryDelay: 1000,
      }),
    });
  }

  /**
   * Get ethers wallet for signing (only if unlocked)
   */
  getEthersWallet(): HDNodeWallet | null {
    if (!this.currentSeed) return null;
    return WalletManager.getWallet(this.currentSeed);
  }

  /**
   * Set active wallet preference
   */
  async setActiveWalletPreference(wallet: 'metamask' | 'wdk'): Promise<void> {
    await this.storage.setItem(STORAGE_KEYS.ACTIVE_WALLET, wallet);
  }

  /**
   * Get active wallet preference
   */
  async getActiveWalletPreference(): Promise<'metamask' | 'wdk'> {
    const stored = await this.storage.getItem(STORAGE_KEYS.ACTIVE_WALLET);
    return (stored === 'metamask' || stored === 'wdk') ? stored : 'metamask';
  }

  // ==========================================
  // Multi-Chain Support Methods (Powered by Tether WDK)
  // ==========================================

  /**
   * Derive address for a specific chain (async version)
   *
   * Calls the backend WDK API for proper derivation when available.
   * Falls back to local ethers-based derivation if API is unavailable.
   */
  static async deriveAddressForChainAsync(
    seed: string,
    chain: NetworkType,
    network: 'mainnet' | 'testnet' = 'testnet',
    apiUrl?: string
  ): Promise<string> {
    try {
      // Try to use the WDK API for proper derivation
      const wdkApi = getWdkApiClient(apiUrl);
      const response = await wdkApi.deriveAddress(seed, chain, network);

      if (response.success && response.address) {
        return response.address;
      }

      console.warn(`WDK API failed for ${chain}, using local derivation:`, response.error);
    } catch (error) {
      console.warn(`WDK API unavailable for ${chain}, using local derivation:`, error);
    }

    // Fallback to local ethers-based derivation
    return WalletManager.deriveAddressForChain(seed, chain);
  }

  /**
   * Format address for non-WDK chains (fallback)
   *
   * Note: This fallback produces PLACEHOLDER addresses derived from the seed.
   * For real blockchain interaction, use the WDK API via deriveAddressForChainAsync().
   * These addresses should NOT be used for receiving funds without verification.
   */
  private static formatAddressForChain(address: string, chain: NetworkType): string {
    // For Ethereum, just return the address as-is
    if (chain === 'ethereum') {
      return address;
    }

    // Generate deterministic placeholder addresses from the Ethereum address
    // These are NOT cryptographically correct for each chain but are:
    // - Deterministic (same seed = same address)
    // - Correctly formatted for each chain
    // - Clearly marked as needing verification

    const addressBytes = address.toLowerCase().replace('0x', '');

    switch (chain) {
      case 'bitcoin': {
        // Bitcoin testnet P2WPKH addresses start with tb1q
        // Format: tb1q + 38 chars of bech32
        // Use first 32 chars of address hash as base for bech32-like encoding
        const btcChars = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';
        let btcAddr = 'tb1q';
        for (let i = 0; i < 38; i++) {
          const idx = parseInt(addressBytes.charAt(i % 40) || '0', 16) % btcChars.length;
          btcAddr += btcChars[idx];
        }
        return btcAddr;
      }

      case 'ton': {
        // TON bounceable address: EQ + 46 chars base64url
        // Total length: 48 chars
        const base64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
        let tonAddr = 'EQ';
        for (let i = 0; i < 46; i++) {
          const idx = parseInt(addressBytes.charAt(i % 40) || '0', 16) * 4 % base64Chars.length;
          tonAddr += base64Chars[idx];
        }
        return tonAddr;
      }

      case 'tron': {
        // TRON address: T + 33 chars base58
        // Total length: 34 chars
        const base58Chars = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
        let tronAddr = 'T';
        for (let i = 0; i < 33; i++) {
          const idx = parseInt(addressBytes.charAt(i % 40) || '0', 16) * 3 % base58Chars.length;
          tronAddr += base58Chars[idx];
        }
        return tronAddr;
      }

      case 'solana': {
        // Solana address: 32-44 chars base58
        // Typically 44 chars
        const base58Chars = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
        let solAddr = '';
        for (let i = 0; i < 44; i++) {
          const idx = parseInt(addressBytes.charAt(i % 40) || '0', 16) * 3 % base58Chars.length;
          solAddr += base58Chars[idx];
        }
        return solAddr;
      }

      case 'spark': {
        // Spark/Lightning: sp1 + 59 chars bech32m
        // Total length: 62 chars
        const bech32Chars = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';
        let sparkAddr = 'sp1q';
        for (let i = 0; i < 58; i++) {
          const idx = parseInt(addressBytes.charAt(i % 40) || '0', 16) % bech32Chars.length;
          sparkAddr += bech32Chars[idx];
        }
        return sparkAddr;
      }

      default:
        return address;
    }
  }

  /**
   * Derive address for a specific chain (sync version for backwards compatibility)
   * Uses ethers for basic derivation, use deriveAddressForChainAsync for WDK
   *
   * Note: For non-Ethereum chains, this produces placeholder addresses that are
   * deterministic but not cryptographically valid. Use WDK API for real addresses.
   */
  static deriveAddressForChain(seed: string, chain: NetworkType): string {
    // Always derive from Ethereum path first to get the base address
    const ethPath = DERIVATION_PATHS['ethereum'];
    const ethNode = HDNodeWallet.fromPhrase(seed, undefined, `${ethPath}/0`);

    if (chain === 'ethereum') {
      return ethNode.address;
    }

    // For other chains, generate formatted placeholder addresses based on the ETH address
    return WalletManager.formatAddressForChain(ethNode.address, chain);
  }

  /**
   * Derive addresses for all enabled chains (sync version)
   */
  deriveAllAddresses(): Partial<MultiChainAddresses> {
    if (!this.currentSeed) {
      throw new Error('Wallet is locked');
    }

    const addresses: Partial<MultiChainAddresses> = {};

    for (const chain of this.config.enabledChains) {
      addresses[chain] = WalletManager.deriveAddressForChain(this.currentSeed, chain);
    }

    this.derivedAddresses = addresses;
    return addresses;
  }

  /**
   * Derive addresses for all enabled chains using native Tether WDK (recommended)
   * This uses the WDK directly without needing a backend API.
   * Returns REAL cryptographically valid addresses for all chains.
   */
  async deriveAllAddressesWithWdk(): Promise<Partial<MultiChainAddresses>> {
    if (!this.currentSeed) {
      throw new Error('Wallet is locked');
    }

    try {
      // Initialize WDK with the current seed
      await this.wdkService.initialize(this.currentSeed);

      // Derive addresses for enabled chains only
      const enabledChainsSet = new Set(this.config.enabledChains);
      const wdkAddresses = await this.wdkService.deriveAllAddresses();

      // Filter to only enabled chains
      const addresses: Partial<MultiChainAddresses> = {};
      for (const [chain, address] of Object.entries(wdkAddresses)) {
        if (enabledChainsSet.has(chain as NetworkType) && address) {
          addresses[chain as NetworkType] = address;
        }
      }

      this.derivedAddresses = addresses;
      return addresses;
    } catch (error) {
      console.error('Native WDK derivation failed:', error);
      throw error;
    }
  }

  /**
   * Derive addresses for all enabled chains using Tether WDK
   * Tries native WDK first, then falls back to API, then to placeholders
   */
  async deriveAllAddressesAsync(): Promise<Partial<MultiChainAddresses>> {
    if (!this.currentSeed) {
      throw new Error('Wallet is locked');
    }

    // Strategy 1: Try native WDK (best - no network dependency)
    try {
      return await this.deriveAllAddressesWithWdk();
    } catch (wdkError) {
      console.warn('Native WDK failed, trying API:', wdkError);
    }

    // Strategy 2: Try WDK API (requires backend running)
    try {
      const wdkApi = getWdkApiClient(this.config.apiUrl);
      const response = await wdkApi.deriveAllAddresses(this.currentSeed, this.config.network);

      if (response.success && response.addresses) {
        // Filter to only enabled chains
        const addresses: Partial<MultiChainAddresses> = {};
        for (const chain of this.config.enabledChains) {
          const address = response.addresses[chain];
          if (address) {
            addresses[chain] = address;
          }
        }
        this.derivedAddresses = addresses;
        return addresses;
      }

      console.warn('WDK API call failed:', response.error);
    } catch (apiError) {
      console.warn('WDK API unavailable:', apiError);
    }

    // Strategy 3: Fallback to placeholders (NOT RECOMMENDED - addresses are not real)
    console.warn('WARNING: Using placeholder addresses. These are NOT valid for receiving funds!');
    return this.deriveAllAddresses();
  }

  /**
   * Get address for a specific chain
   */
  getAddressForChain(chain: NetworkType): string | null {
    if (!this.currentSeed) {
      return this.derivedAddresses[chain] || null;
    }

    if (!this.derivedAddresses[chain]) {
      this.derivedAddresses[chain] = WalletManager.deriveAddressForChain(this.currentSeed, chain);
    }

    return this.derivedAddresses[chain] || null;
  }

  /**
   * Get all derived addresses
   */
  getAllAddresses(): Partial<MultiChainAddresses> {
    return { ...this.derivedAddresses };
  }

  /**
   * Set the selected chain
   */
  setSelectedChain(chain: NetworkType): void {
    if (!this.config.enabledChains.includes(chain)) {
      throw new Error(`Chain ${chain} is not enabled`);
    }
    this.selectedChain = chain;
  }

  /**
   * Get the currently selected chain
   */
  getSelectedChain(): NetworkType {
    return this.selectedChain;
  }

  /**
   * Get enabled chains
   */
  getEnabledChains(): NetworkType[] {
    return [...this.config.enabledChains];
  }

  /**
   * Get chain configuration
   */
  getChainConfig(chain: NetworkType): NetworkConfig {
    return getNetworkConfig(chain, this.config.network === 'testnet');
  }

  /**
   * Fetch balance for a specific chain
   * Note: Currently only Ethereum is implemented
   */
  async fetchBalanceForChain(chain: NetworkType): Promise<ChainBalance> {
    const address = this.getAddressForChain(chain);
    if (!address) {
      throw new Error(`No address for chain ${chain}`);
    }

    const networkConfig = this.getChainConfig(chain);
    let balance = '0';

    // Fetch balance based on chain type
    if (chain === 'ethereum') {
      const viemChain = this.config.network === 'mainnet' ? mainnet : sepolia;
      const client = createPublicClient({
        chain: viemChain,
        transport: http(this.config.rpcUrl, {
          timeout: 15000, // 15 second timeout
          retryCount: 2,
          retryDelay: 1000,
        }),
      });

      try {
        const rawBalance = await client.getBalance({
          address: address as `0x${string}`,
        });
        balance = viemFormatEther(rawBalance);
      } catch (error) {
        console.warn(`Failed to fetch ${chain} balance:`, error);
        // Balance remains '0' on error
      }
    }
    // TODO: Implement balance fetching for other chains
    // - Bitcoin: Use blockstream API
    // - TON: Use toncenter API
    // - TRON: Use trongrid API
    // - Solana: Use Solana JSON RPC
    // - Spark: Use Spark SDK

    return {
      chain,
      symbol: networkConfig.nativeCurrency.symbol,
      balance,
      balanceUsd: 0, // TODO: Implement price fetching
      address,
      decimals: networkConfig.nativeCurrency.decimals,
    };
  }

  /**
   * Fetch balances for all enabled chains
   */
  async fetchAllBalances(): Promise<ChainBalance[]> {
    const balances: ChainBalance[] = [];

    for (const chain of this.config.enabledChains) {
      try {
        const balance = await this.fetchBalanceForChain(chain);
        balances.push(balance);
      } catch (error) {
        console.error(`Failed to fetch balance for ${chain}:`, error);
        // Add placeholder balance on error
        const networkConfig = this.getChainConfig(chain);
        balances.push({
          chain,
          symbol: networkConfig.nativeCurrency.symbol,
          balance: '0',
          balanceUsd: 0,
          address: this.getAddressForChain(chain) || '',
          decimals: networkConfig.nativeCurrency.decimals,
        });
      }
    }

    return balances;
  }

  /**
   * Get extended wallet state with multi-chain info
   */
  getExtendedState(): WalletState {
    return {
      isInitialized: this.derivedAddress !== null,
      isLocked: this.currentSeed === null && this.derivedAddress !== null,
      address: this.derivedAddress,
      balance: null,
      addresses: this.derivedAddresses,
      selectedChain: this.selectedChain,
    };
  }
}
